/* eslint-disable import/prefer-default-export */
import React, { useState, useEffect, useRef } from 'react';
import { AnimatePresence } from 'framer-motion';
import { General } from 'icons/untitledui';

import type { TopBarMenuItem } from 'topBar/components/TopBar';
import { DropdownMenu } from 'shared/components/dropdown/DropdownMenu';
import * as Menu from 'topBar/styles/TopBarMenu.style';
import * as Styled from 'topBar/styles/SingleItemAction.style';
import * as Text from 'theme/components/Text.style';

interface SingleItemActionProps {
  item: TopBarMenuItem;
  menuButtonAriaLabel: string;
}

export function SingleItemAction({
  item,
  menuButtonAriaLabel,
}: SingleItemActionProps): JSX.Element {
  const [showingSuccess, setShowingSuccess] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    []
  );

  if (item.subItems) {
    const dropdownItems = item.subItems.map((subItem) => ({
      label: <Text.P2>{subItem.label}</Text.P2>,
      disabled: subItem.disabled,
      disabledReason: subItem.disabledReason,
      icon: subItem.icon,
      primary: subItem.primary,
      destructive: subItem.destructive,
      onSelect: ({ close }: { close: () => void }) => {
        if (subItem.disabled) return;
        subItem.onClick();
        close();
      },
    }));

    return (
      <Menu.MenuWrapper>
        <DropdownMenu
          isOpen={isMenuOpen}
          onOpenChange={setIsMenuOpen}
          usePortal
          items={dropdownItems}
          headerLabel={item.subHeaderLabel}
          renderTrigger={({ isOpen, toggle }) => (
            <Menu.AnimatedMenuButton
              key="button"
              onClick={toggle}
              aria-haspopup="true"
              aria-expanded={isOpen}
              aria-label={item.label || menuButtonAriaLabel}
              role="button"
              tabIndex={item.disabled ? -1 : 0}
              disabled={item.disabled}
              $selected={item.selectedStyling}
              $primary={item.primary}
              {...Menu.menuButtonMotion}
            >
              {item.primary ? (
                <>
                  <Text.P2Semibold>{item.label}</Text.P2Semibold>
                  {item.icon}
                </>
              ) : (
                item.icon || <General.DotsHorizontal size={20} />
              )}
            </Menu.AnimatedMenuButton>
          )}
        />
      </Menu.MenuWrapper>
    );
  }

  const handleClick = (e: React.SyntheticEvent<HTMLElement>) => {
    e.stopPropagation();
    if (item.disabled) return;

    item.onClick();

    if (item.successMessage) {
      setShowingSuccess(true);
      timeoutRef.current = setTimeout(() => {
        setShowingSuccess(false);
      }, 3000);
    }
  };

  return (
    <Menu.MenuWrapper>
      <AnimatePresence mode="wait">
        {showingSuccess && item.successMessage ? (
          <Styled.SuccessPill
            key="success"
            initial={{
              ...Menu.menuButtonMotion.initial,
              borderRadius: '50%',
            }}
            animate={{
              ...Menu.menuButtonMotion.animate,
              borderRadius: '22px',
            }}
            exit={{ ...Menu.menuButtonMotion.exit, borderRadius: '50%' }}
          >
            <Text.P2Semibold>{item.successMessage}</Text.P2Semibold>
            <General.CheckCircleBroken size={20} />
          </Styled.SuccessPill>
        ) : (
          <Menu.AnimatedMenuButton
            key="button"
            onClick={handleClick}
            aria-label={item.label || menuButtonAriaLabel}
            role="button"
            tabIndex={item.disabled ? -1 : 0}
            disabled={item.disabled}
            $selected={item.selectedStyling}
            $primary={item.primary}
            onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
              if ((e.key === 'Enter' || e.key === ' ') && !item.disabled) {
                e.preventDefault();
                handleClick(e);
              }
            }}
            {...Menu.menuButtonMotion}
          >
            {item.primary ? (
              <>
                <Text.P2Semibold>{item.label}</Text.P2Semibold>
                {item.icon}
              </>
            ) : (
              item.icon || <General.DotsHorizontal size={20} />
            )}
          </Menu.AnimatedMenuButton>
        )}
      </AnimatePresence>
    </Menu.MenuWrapper>
  );
}
